import { Form, FormField } from "@babackapp/ui/components/form";
import FormInput from "@babackapp/react-hook-forms-components/form-input";
import FormInputRow from "@babackapp/react-hook-forms-components/form-input-row";
import FormMultiOptionsSelector from "@babackapp/react-hook-forms-components/form-multi-options-selector";
import FormOptionsSelector from "@babackapp/react-hook-forms-components/form-option-selector";
import { SettingsFormPageButtonBar } from "@babackapp/react-hook-forms-components/form-page";
import FormSwitch from "@babackapp/react-hook-forms-components/form-switch";
import FormTagsInput from "@babackapp/react-hook-forms-components/form-tags-input";
import useTranslation from "@babackapp/translations/use-translation";
import MetafieldForm from "./metafield-form.tsx";
import {
  type CustomerPortalFormValues,
  useCustomerPortalBasicsForm,
} from "@/pages/(app)/customer-portal/components/customer-portal-basics/use-customer-portal-basics-form.ts";
import { CustomerSegmentsForm } from "@/pages/(app)/customer-portal/components/customer-portal-basics/customer-segments-form.tsx";
import { useShopFlag } from "@/hooks/use-flag.ts";
import ShopLocationSelect from "@/components/shop-location-select/shop-location-select";
import useShopifyShopLocations from "@babackapp/private-api-client/client/use-shopify-shop-locations";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { OverrideRulesManager } from "@/components/override-rules";

type CustomerPortalBasicsFormProps = {
  onSubmit: (data: CustomerPortalFormValues) => void;
  workflowLogisticianActivated: boolean;
  defaultValues: CustomerPortalFormValues;
};

export default function CustomerPortalBasicsForm({
  onSubmit,
  defaultValues,
}: CustomerPortalBasicsFormProps) {
  const t = useTranslation();
  const form = useCustomerPortalBasicsForm({
    defaultValues: defaultValues,
  });
  const isCarrierTagConditionEnabled = useShopFlag(
    "isCarrierTagConditionEnabled",
  );

  // For restock location selection
  const { data: shop } = useCurrentShopSuspense();
  const { data: locations } = useShopifyShopLocations(shop.id);

  const refundInitialShippingOptions = [
    {
      label: t("settings.config.firstLineItem"),
      key: "firstLineItem",
    },
    {
      label: t("settings.config.lastLineItem"),
      key: "lastLineItem",
    },
    { label: t("settings.config.never"), key: "never" },
  ];

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col justify-start gap-8 mt-8"
      >
        <FormField
          name="contactLink"
          render={({ field }) => (
            <FormInput
              label={t(
                "settings.customerPortal.basics.fields.contactLink.title",
              )}
              description={t(
                "settings.customerPortal.basics.fields.contactLink.description",
              )}
              {...field}
            />
          )}
        />

        <FormField
          name="languages"
          render={({ field }) => (
            <FormMultiOptionsSelector
              hideKey
              label={t("settings.customerPortal.basics.fields.languages.title")}
              description={t(
                "settings.customerPortal.basics.fields.languages.description",
              )}
              options={availableLanguages}
              values={field.value ?? []}
              onChange={(values) => field.onChange(values)}
            />
          )}
        />

        <FormField
          name="webOrderOnly"
          render={({ field }) => (
            <FormSwitch
              label={t(
                "settings.customerPortal.basics.fields.webOrderOnly.title",
              )}
              description={t(
                "settings.customerPortal.basics.fields.webOrderOnly.description",
              )}
              {...field}
            />
          )}
        />

        <MetafieldForm
          name={"nonReturnableMetafieldItems"}
          label={t("settings.config.tagsForMetafieldItems.title")}
          description={t("settings.config.tagsForMetafieldItems.description")}
        />

        <FormField
          name="tagsForNotAcceptedOrder"
          render={({ field }) => (
            <FormTagsInput
              label={t(
                "settings.customerPortal.basics.fields.tagsForNotAcceptedOrder.title",
              )}
              description={t(
                "settings.customerPortal.basics.fields.tagsForNotAcceptedOrder.description",
              )}
              {...field}
            />
          )}
        />

        <FormField
          name="nonReturnableItems"
          render={({ field }) => (
            <FormTagsInput
              label={t(
                "settings.customerPortal.basics.fields.nonReturnableItems.title",
              )}
              description={t(
                "settings.customerPortal.basics.fields.nonReturnableItems.description",
              )}
              {...field}
            />
          )}
        />

        <FormInputRow>
          <FormField
            name="restockOnReturn"
            render={({ field }) => (
              <FormSwitch
                label={t(
                  "settings.customerPortal.basics.fields.restockOnReturn.title",
                )}
                description={t(
                  "settings.customerPortal.basics.fields.restockOnReturn.description",
                )}
                {...field}
              />
            )}
          />
        </FormInputRow>

        {form.getValues().restockOnReturn && (
          <FormField
            name="restockLocationId"
            render={({ field: locationField }) => {
              const selectedLocation =
                locations?.find((l) => l.id === locationField.value) ?? null;
              return (
                <ShopLocationSelect
                  value={selectedLocation}
                  onChange={(location) =>
                    locationField.onChange(location?.id ?? null)
                  }
                  allowNone={false}
                  description={t(
                    "settings.customerPortal.basics.fields.restockLocation.description",
                  )}
                />
              );
            }}
          />
        )}

        <FormField
          name="clientCanUpdatePersonalInformation"
          render={({ field }) => (
            <FormSwitch
              label={t(
                "settings.customerPortal.basics.fields.clientCanUpdatePersonalInformation.title",
              )}
              description={t(
                "settings.customerPortal.basics.fields.clientCanUpdatePersonalInformation.description",
              )}
              {...field}
            />
          )}
        />
        <div>
          <FormField
            name="refundInitialShippingRule"
            render={({ field }) => (
              <FormOptionsSelector
                label={t("settings.config.refundInitialShippingRule")}
                description={t(
                  "settings.config.refundInitialShippingRuleDescription",
                )}
                options={refundInitialShippingOptions}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <OverrideRulesManager
            actionType={"OVERRIDE_SHIPPING_REFUND"}
            actionValueOptions={refundInitialShippingOptions}
          />
        </div>
        <FormField
          name="oneResolutionPerReturn"
          render={({ field }) => (
            <FormSwitch
              label={t("settings.shop.oneResolutionPerReturn.title")}
              description={t(
                "settings.shop.oneResolutionPerReturn.description",
              )}
              {...field}
            />
          )}
        />

        {isCarrierTagConditionEnabled && (
          <FormField
            name="singleArticleReturnOnly"
            render={({ field }) => (
              <FormSwitch
                label={t("settings.shop.singleArticleReturnOnly.title")}
                description={t(
                  "settings.shop.singleArticleReturnOnly.description",
                )}
                {...field}
                value={Boolean(field.value)}
              />
            )}
          />
        )}

        <FormInputRow>
          <FormField
            name="canCancelReturnRequest"
            render={({ field }) => (
              <FormSwitch
                label={t(
                  "settings.customerPortal.basics.fields.canCancelReturnRequest.title",
                )}
                description={t(
                  "settings.customerPortal.basics.fields.canCancelReturnRequest.description",
                )}
                {...field}
              />
            )}
          />
        </FormInputRow>

        <FormInputRow>
          <FormField
            name="allowOnlyUniqueReturnRequest"
            render={({ field }) => (
              <FormSwitch
                label={t(
                  "settings.customerPortal.basics.fields.allowOnlyUniqueReturnRequest.title",
                )}
                description={t(
                  "settings.customerPortal.basics.fields.allowOnlyUniqueReturnRequest.description",
                )}
                {...field}
              />
            )}
          />
        </FormInputRow>
        <CustomerSegmentsForm />

        <SettingsFormPageButtonBar />
      </form>
    </Form>
  );
}

const availableLanguages = [
  { key: "fr-FR", label: "Français" },
  { key: "en-US", label: "English" },
  { key: "de-DE", label: "Deutsch" },
  { key: "it-IT", label: "Italiano" },
  { key: "es-ES", label: "Spanish" },
  { key: "nl-NL", label: "Nederlands" },
  { key: "ro-RO", label: "Romanian" },
  { key: "cs-CZ", label: "Czech" },
  { key: "da-DK", label: "Danish" },
  { key: "fi-FI", label: "Finnish" },
  { key: "el-GR", label: "Greek" },
  { key: "lv-LV", label: "Latvian" },
  { key: "sv-SE", label: "Swedish" },
  { key: "no-NO", label: "Norwegian" },
  { key: "pl-PL", label: "Polish" },
  { key: "ko-KR", label: "Korean" },
  { key: "ja-JP", label: "Japanese" },
  { key: "pt-BR", label: "Portuguese (Brazil)" },
  { key: "pt-PT", label: "Portuguese (Portugal)" },
  { key: "zh-Hans", label: "Chinese (Simplified)" },
  { key: "zh-Hant", label: "Chinese (Traditional)" },
];
